import {
  ALREADY_SENT_IN_LAST_MINUTE,
  AUTHORITY_TOO_LONG_1,
  AUTHORITY_TOO_LONG_2,
  BIOMETRY_EXPIRED,
  DAY_LIMIT_GET_SMS,
  E_WALLET_IS_NOT_IDENTIFIED,
  E_WALLET_NOT_VALID,
  E_WALLET_OWNER_IS_NOT_MATCHED_TO_CLIENT,
  ERROR_VALUE_IN_CYRILLIC,
  INVALID_EMAIL_DOMAIN,
  INVALID_EMAIL_VERIFICATION_CODE,
  INVALID_VERIFICATION_CODE,
  NOT_SENT_CODE,
  NOT_UNIQUE,
  NOT_VALID_EMAIL,
  NOT_VALID_EMAIL_2,
  NOT_VALID_FORMAT_ID_NUMBER,
  NOT_VALID_PASSWORD,
  NOT_VALID_PHONE_NUMBER,
  PASSWORD_CHARS_COMPLEXITY,
  PASSWORD_TOO_LONG,
  PASSWORD_TOO_SHORT,
  SMS_CODE_VALIDATION_ERROR,
  TELEPHONE_ID_USED,
  TOO_FREQUENT_ATTEMPTS,
  TOO_MANY_ATTEMTPS,
  UNDER_AGE,
} from '~/utils/errors';
import {
  EMAIL_CODE_LENGTH,
  MOBILE_PHONE_LENGTH,
  PHONE_CODE_LENGTH,
  SMS_CODE_LENGTH,
  SOCIAL_FUND_OTP_CODE_LENGTH,
} from '~/utils/consts';

export default ({ app }) => {
  function getMessages(key) {
    const { availableLocales, messages } = app.$lang;

    return availableLocales.reduce((errorMessages, locale) => {
      const message = messages[locale].rules;
      errorMessages[locale] = message ? message[key] : '';

      return errorMessages;
    }, {});
  }

  app.$validator.define({
    required: {
      rule({ value }) {
        return !value;
      },
      get messages() {
        return getMessages('required');
      },
      selector: 'error-required',
    },
    requiredWithZero: {
      rule({ value }) {
        return typeof value !== 'number';
      },
      get messages() {
        return getMessages('required-with-zero');
      },
      selector: 'error-required-with-zero',
    },
    idNumberLength: {
      rule({ value }) {
        return value.length !== 14;
      },
      get messages() {
        return getMessages('is-number-length');
      },
      selector: 'error-id-number-length',
    },
    mail: {
      rule({ value }) {
        /* eslint-disable-next-line */
        const REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return !REGEX.test(value);
      },
      get messages() {
        return getMessages('mail');
      },
      selector: 'error-mail',
    },
    unique: {
      code: NOT_UNIQUE,
      messages: {},
    },
    under: {
      code: UNDER_AGE,
      get messages() {
        return getMessages('under');
      },
    },
    notClientEmail: {
      rule({ value }) {
        return value === app.$client.profile.email;
      },
      messages: {
        en: "Can't use own email",
      },
    },
    notValidEmail: {
      code: NOT_VALID_EMAIL,
      get messages() {
        return getMessages('not-valid-email');
      },
    },
    notValidEmail_2: {
      code: NOT_VALID_EMAIL_2,
      get messages() {
        return getMessages('not-valid-email-2');
      },
    },
    invalidEmailDomain: {
      rule({ errorStatus }) {
        return errorStatus === INVALID_EMAIL_DOMAIN;
      },
      get messages() {
        return getMessages('invalid-email-domain');
      },
    },
    notCyrillic: {
      code: ERROR_VALUE_IN_CYRILLIC,
      get messages() {
        return getMessages('not-cyrillic');
      },
    },
    phoneFormat: {
      rule({ complete }) {
        return !complete;
      },
      get messages() {
        return getMessages('phone-format');
      },
    },
    notValidPhoneNumber: {
      code: NOT_VALID_PHONE_NUMBER,
      get messages() {
        return getMessages('not-valid-phone-number');
      },
    },
    wrongPassword: {
      rule({ secondValue, value }) {
        return value !== secondValue;
      },
      get messages() {
        return getMessages('wrong-password');
      },
      selector: 'error-wrong-password',
    },
    wrongPasswordLength: {
      rule({ value }) {
        return value.length < 4;
      },
      get messages() {
        return getMessages('wrong-password-length');
      },
      selector: 'error-wrong-password-length',
    },
    smsNotPassed: {
      rule({ value }) {
        return value.length !== 4;
      },
      get messages() {
        return getMessages('sms-not-passed');
      },
    },
    notValidPassword: {
      code: NOT_VALID_PASSWORD,
      get messages() {
        return getMessages('not-valid-password');
      },
      selector: 'error-not-valid-password',
    },
    passwordTooShort: {
      code: PASSWORD_TOO_SHORT,
      get messages() {
        return getMessages('password-too-short');
      },
      rule({ value }) {
        return value.length < 6;
      },
      selector: 'error-password-too-short',
    },
    passwordTooLong: {
      code: PASSWORD_TOO_LONG,
      get messages() {
        return getMessages('password-too-long');
      },
      rule({ value }) {
        return value.length > 50;
      },
      selector: 'error-password-too-long',
    },
    passwordComplexity: {
      code: PASSWORD_CHARS_COMPLEXITY,
      get messages() {
        return getMessages('password-complexity');
      },
      selector: 'error-password-complexity',
    },
    passwordWithNumber: {
      rule({ value }) {
        return !/[0-9]/.test(value);
      },
      get messages() {
        return getMessages('password-least-one-number');
      },
      selector: 'error-password-number',
    },
    passwordWithLowerCase: {
      rule({ value }) {
        return !/[a-z]/.test(value);
      },
      get messages() {
        return getMessages('password-least-one-lower');
      },
      selector: 'error-password-lower',
    },
    passwordWithUpperCase: {
      rule({ value }) {
        return !/[A-Z]/.test(value);
      },
      get messages() {
        return getMessages('password-least-one-upper');
      },
      selector: 'error-password-upper',
    },
    passportNumber: {
      rule({ value }) {
        return value.length !== 9;
      },
      get messages() {
        return getMessages('passport-number');
      },
    },
    passportIdUsed: {
      code: NOT_UNIQUE,
      get messages() {
        return getMessages('passport-id-used');
      },
    },
    passportIdNotValid: {
      code: NOT_VALID_FORMAT_ID_NUMBER,
      get messages() {
        return getMessages('passport-id-not-valid');
      },
    },
    incomeLength: {
      rule({ value }) {
        return parseInt(value) < 1000;
      },
      get messages() {
        return getMessages('income-length');
      },
    },
    additionalIncomeLength: {
      rule({ value }) {
        return parseInt(value) < 1000;
      },
      get messages() {
        return getMessages('additional-income-length');
      },
    },
    sameAdditionalPhone: {
      code: TELEPHONE_ID_USED,
      get messages() {
        return getMessages('same-additional-phone');
      },
    },
    bankAccountLength: {
      rule({ value }) {
        return value.length !== 16;
      },
      get messages() {
        return getMessages('bank-account-length');
      },
    },
    date: {
      rule({ value }) {
        return value.length !== 10;
      },
      get messages() {
        return getMessages('date');
      },
    },
    authorityTooLong_1: {
      code: AUTHORITY_TOO_LONG_1,
      get messages() {
        return getMessages('authority-too-long-1');
      },
    },
    authorityTooLong_2: {
      code: AUTHORITY_TOO_LONG_2,
      get messages() {
        return getMessages('authority-too-long-2');
      },
    },
    workCompanyLength: {
      rule({ value }) {
        return value.length <= 2;
      },
      get messages() {
        return getMessages('work-company-length');
      },
    },
    additionalNameLength: {
      rule({ value }) {
        return value.length <= 1;
      },
      get messages() {
        return getMessages('additional-name-length');
      },
    },
    notValidEwallet: {
      code: E_WALLET_NOT_VALID,
      get messages() {
        return getMessages('not-valid-ewallet');
      },
    },
    eWalletIsUsed: {
      code: NOT_UNIQUE,
      get messages() {
        return getMessages('e-wallet-is-used');
      },
    },
    bankIsUsed: {
      code: NOT_UNIQUE,
      get messages() {
        return getMessages('bank-is-used');
      },
    },
    eWalletIsNotIdentified: {
      code: E_WALLET_IS_NOT_IDENTIFIED,
      get messages() {
        return getMessages('e-wallet-is-not-identified');
      },
    },
    eWalletIsNotMatchedToClient: {
      code: E_WALLET_OWNER_IS_NOT_MATCHED_TO_CLIENT,
      get messages() {
        return getMessages('e-wallet-is-not-matched-to-client');
      },
    },
    phoneLength: {
      rule({ value }) {
        return value.length !== MOBILE_PHONE_LENGTH;
      },
      get messages() {
        return getMessages('phone-length');
      },
    },
    phoneCodeLength: {
      rule({ value = '' }) {
        return value.length !== PHONE_CODE_LENGTH;
      },
      get messages() {
        return getMessages('phone-code-length');
      },
    },
    emailCodeLength: {
      rule({ value = '' }) {
        return value.length !== EMAIL_CODE_LENGTH;
      },
      get messages() {
        return getMessages('email-code-length');
      },
    },
    expiredBiometry: {
      code: BIOMETRY_EXPIRED,
      rule({ valid }) {
        return !valid;
      },
      get messages() {
        return getMessages('expired-biometry');
      },
    },
    codeLength: {
      rule({ value = '' }) {
        return value.length !== SMS_CODE_LENGTH;
      },
      get messages() {
        return getMessages('code-length');
      },
    },
    otpCodeLength: {
      rule({ value = '' }) {
        return value.length !== SOCIAL_FUND_OTP_CODE_LENGTH;
      },
      get messages() {
        return getMessages('otp-code-length');
      },
    },
    invalidVerificationCode: {
      code: INVALID_VERIFICATION_CODE,
      rule({ valid }) {
        return !valid;
      },
      get messages() {
        return getMessages('invalid-verification-code');
      },
    },
    invalidEmailVerificationCode: {
      code: INVALID_EMAIL_VERIFICATION_CODE,
      rule({ valid }) {
        return !valid;
      },
      get messages() {
        return getMessages('invalid-email-verification-code');
      },
    },
    smsCodeNotPassed: {
      rule({ errorStatus }) {
        return errorStatus === SMS_CODE_VALIDATION_ERROR;
      },
      get messages() {
        return getMessages('sms-code-not-passed');
      },
    },
    notSentCode: {
      rule({ errorStatus }) {
        return errorStatus === NOT_SENT_CODE;
      },
      get messages() {
        return getMessages('not-sent-code');
      },
    },
    rolloverSmsFrequentAttempts: {
      code: TOO_FREQUENT_ATTEMPTS,
      messages: {
        ru: 'Код был отправлен менее минуты назад',
        kg: 'Код бир мүнөткө жетпеген убакыт мурун жөнөтүлгөн',
      },
    },
    rolloverSmsManyAttempts: {
      code: TOO_MANY_ATTEMTPS,
      messages: {
        ru: 'Превышен лимит попыток отправки кода',
        kg: 'Кодду жөнөтүү аракетинин чегинен ашты',
      },
    },
    sentInLastMinute: {
      code: ALREADY_SENT_IN_LAST_MINUTE,
      get messages() {
        return getMessages('sent-in-last-minute');
      },
    },
    getSmsDayLimit: {
      code: DAY_LIMIT_GET_SMS,
      get messages() {
        return getMessages('get-sms-day-limit');
      },
    },
  });
};
